/**
 * MARQUE PRODUCTS API
 * Recupere les produits d'une marque via Shopify Storefront API
 * en filtrant par le metafield custom.marque (reference metaobject)
 */

(function() {
  'use strict';

  /**
   * Configuration API Shopify Storefront
   */
  const SHOPIFY_CONFIG = {
    get storefrontToken() {
      const meta = document.querySelector('meta[name="shopify-storefront-token"]');
      return meta ? meta.content : null;
    },
    get shopDomain() {
      return window.Shopify ? window.Shopify.shop : null;
    },
    apiVersion: '2025-10'
  };

  /**
   * Cache des produits pour eviter requetes repetees
   */
  const BrandProductCache = {
    data: {},
    ttl: 10 * 60 * 1000, // 10 minutes

    set(key, value) {
      this.data[key] = {
        value: value,
        timestamp: Date.now()
      };
    },

    get(key) {
      const cached = this.data[key];
      if (!cached) return null;

      if (Date.now() - cached.timestamp > this.ttl) {
        delete this.data[key];
        return null;
      }

      return cached.value;
    }
  };

  /**
   * Recuperer les produits par marque via Storefront API
   * Charge les produits avec leur metafield marque et filtre cote client
   * @param {string} brandGid - GID complet du metaobject marque (gid://shopify/Metaobject/XXX)
   * @param {number} limit - Nombre max de produits a afficher
   * @returns {Promise<Array>} Liste de produits filtres
   */
  async function fetchProductsByBrand(brandGid, limit = 50) {
    try {
      console.log('[Marque Products API] Fetch produits pour marque:', brandGid);

      const cacheKey = `brand_${brandGid}_${limit}`;
      const cached = BrandProductCache.get(cacheKey);
      if (cached) {
        console.log('[Marque Products API] Produits recuperes du cache');
        return cached;
      }

      if (!SHOPIFY_CONFIG.storefrontToken || !SHOPIFY_CONFIG.shopDomain) {
        throw new Error('Configuration Shopify Storefront API manquante');
      }

      const query = `
        query getProductsWithBrand($first: Int!, $after: String) {
          products(first: $first, after: $after) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              node {
                id
                title
                handle
                availableForSale
                marque: metafield(namespace: "custom", key: "marque") {
                  value
                  reference {
                    ... on Metaobject {
                      id
                    }
                  }
                }
                priceRange {
                  minVariantPrice {
                    amount
                    currencyCode
                  }
                }
                compareAtPriceRange {
                  minVariantPrice {
                    amount
                    currencyCode
                  }
                }
                images(first: 2) {
                  edges {
                    node {
                      url
                      altText
                      width
                      height
                    }
                  }
                }
                variants(first: 1) {
                  edges {
                    node {
                      id
                      availableForSale
                    }
                  }
                }
              }
            }
          }
        }
      `;

      console.log('[Marque Products API] Requete Storefront API (pagination par curseur)...');

      let allEdges = [];
      let hasNextPage = true;
      let cursor = null;

      while (hasNextPage) {
        const variables = { first: 250 };
        if (cursor) variables.after = cursor;

        const response = await fetch(`https://${SHOPIFY_CONFIG.shopDomain}/api/${SHOPIFY_CONFIG.apiVersion}/graphql.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': SHOPIFY_CONFIG.storefrontToken
          },
          body: JSON.stringify({ query, variables })
        });

        if (!response.ok) {
          throw new Error(`Erreur HTTP ${response.status}`);
        }

        const data = await response.json();

        if (data.errors) {
          console.error('[Marque Products API] Erreurs GraphQL:', data.errors);
          throw new Error('Erreur GraphQL: ' + data.errors[0]?.message);
        }

        const page = data.data.products;
        allEdges = allEdges.concat(page.edges);
        hasNextPage = page.pageInfo.hasNextPage;
        cursor = page.pageInfo.endCursor;

        console.log('[Marque Products API] Page chargee:', page.edges.length, '| Total:', allEdges.length, '| Encore:', hasNextPage);
      }

      const filteredEdges = allEdges.filter(edge => {
        const marqueMetafield = edge.node.marque;
        if (!marqueMetafield) return false;

        if (marqueMetafield.reference && marqueMetafield.reference.id === brandGid) {
          return true;
        }

        if (marqueMetafield.value) {
          if (marqueMetafield.value === brandGid) return true;
          if (marqueMetafield.value.includes(brandGid)) return true;
          const brandIdMatch = brandGid.match(/\/(\d+)$/);
          if (brandIdMatch && marqueMetafield.value.includes(brandIdMatch[1])) return true;
        }

        return false;
      });

      console.log('[Marque Products API] Produits filtres pour marque:', filteredEdges.length);

      const products = filteredEdges.slice(0, limit).map(edge => {
        const node = edge.node;
        const price = parseFloat(node.priceRange.minVariantPrice.amount);
        const compareAtPrice = parseFloat(node.compareAtPriceRange.minVariantPrice.amount);

        return {
          id: node.id,
          title: node.title,
          handle: node.handle,
          url: `/products/${node.handle}`,
          availableForSale: node.availableForSale,
          price: price,
          compareAtPrice: compareAtPrice > price ? compareAtPrice : null,
          currency: node.priceRange.minVariantPrice.currencyCode,
          images: node.images.edges.map(img => ({
            url: img.node.url,
            alt: img.node.altText || node.title,
            width: img.node.width,
            height: img.node.height
          })),
          variantId: node.variants.edges[0]?.node.id || null
        };
      });

      console.log('[Marque Products API] Produits prets a afficher:', products.length);

      // Mettre en cache
      BrandProductCache.set(cacheKey, products);

      return products;

    } catch (error) {
      console.error('[Marque Products API] Erreur:', error);
      return [];
    }
  }

  /**
   * Formater le prix selon le format Shopify
   * @param {number} amount - Montant
   * @param {string} currency - Code devise
   * @returns {string} Prix formate
   */
  function formatMoney(amount, currency = 'EUR') {
    // Utiliser le format Shopify si disponible
    if (window.theme && window.theme.moneyFormat) {
      const formattedAmount = amount.toFixed(2).replace('.', ',');
      return window.theme.moneyFormat.replace('{{amount}}', formattedAmount)
        .replace('{{amount_with_comma_separator}}', formattedAmount)
        .replace('{{amount_no_decimals}}', Math.round(amount).toString())
        .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(amount).toString());
    }

    // Fallback simple
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency
    }).format(amount);
  }

  /**
   * Generer le HTML d'une carte produit (structure identique a la page collection)
   * @param {Object} product - Donnees produit
   * @param {number} index - Index du produit pour l'animation
   * @returns {string} HTML de la carte
   */
  function renderProductCard(product, index = 0) {
    const image = product.images[0] || null;
    const imageUrl = image ? `${image.url}&width=400` : '';
    const imageAlt = image ? image.alt : product.title;
    const hasDiscount = product.compareAtPrice && product.compareAtPrice > product.price;
    const discountPercent = hasDiscount ? Math.round((1 - product.price / product.compareAtPrice) * 100) : 0;

    let priceHtml = '';
    if (hasDiscount) {
      priceHtml = `
        <span class="product-item__price-main">
          <s class="t-subdued">${formatMoney(product.compareAtPrice, product.currency)}</s>
          <span class="sale" data-price="">${formatMoney(product.price, product.currency)}</span>
        </span>
      `;
    } else {
      priceHtml = `
        <span class="product-item__price-main">
          <span data-price="">${formatMoney(product.price, product.currency)}</span>
        </span>
      `;
    }

    let badgeHtml = '';
    if (hasDiscount) {
      badgeHtml = `
        <div class="product-badges" data-badge-shape="rectangle" data-badge-vertical-position="top" data-badge-horizontal-position="left">
          <div class="product-badge" data-type="sale">
            <span class="product-badge__inner badge-shape" data-badge-shape="rectangle">
              <span class="displayed-discount-wrapper" data-hidden="false" data-has-discount="true">
                <span data-badge-shape="rectangle">${discountPercent}% de reduction</span>
              </span>
            </span>
          </div>
        </div>
      `;
    }

    let imageHtml = '';
    if (image) {
      imageHtml = `
        <div class="image product-item__image aspect-ratio--square loaded" style="--object-fit: contain;">
          <img
            alt="${imageAlt}"
            class="image__img"
            loading="lazy"
            width="400"
            height="400"
            src="${imageUrl}"
            srcset="${image.url}&width=150 150w, ${image.url}&width=200 200w, ${image.url}&width=300 300w, ${image.url}&width=400 400w"
            sizes="(max-width: 720px) calc((90vw - 12px) / 2), (max-width: 1400px) calc((93.4vw - (12px * 3)) / 4), 270px"
          >
        </div>
      `;
    } else {
      imageHtml = `
        <div class="placeholder-image image aspect-ratio--square product-item__image">
          <svg class="placeholder-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5">
            <path d="M324.5 212.7H203c-1.6 0-2.8 1.3-2.8 2.8V308c0 1.6 1.3 2.8 2.8 2.8h121.6c1.6 0 2.8-1.3 2.8-2.8v-92.5c0-1.6-1.3-2.8-2.9-2.8zm1.1 95.3c0 .6-.5 1.1-1.1 1.1H203c-.6 0-1.1-.5-1.1-1.1v-92.5c0-.6.5-1.1 1.1-1.1h121.6c.6 0 1.1.5 1.1 1.1V308z"></path>
          </svg>
        </div>
      `;
    }

    return `
      <div class="product-item animation--item animation--item-revealed"
           style="--z-index-item: ; --delay-offset-multiplier: ${index};"
           data-product-item-alignment="center"
           data-price-position="below_title">
        <div class="product-item__inner">
          <div class="product-item__media-wrapper">
            <div class="product-item__media" data-aspect-ratio="square">
              <a class="product-item__image-link" href="${product.url}" aria-label="${product.title}">
                ${imageHtml}
              </a>
              ${badgeHtml}
            </div>
          </div>

          <div class="product-item__meta">
            <div class="product-item__text ta-c">
              <div class="product-item__text_group_primary">
                <h3 class="product-item__product-title fs-product-title ff-heading" data-line-limit="1">
                  <a href="${product.url}">${product.title}</a>
                </h3>

                <div class="product-item__price-wrap" data-position="below_title">
                  <p class="product-item__price ff-product-price" data-position="below_title">
                    ${priceHtml}
                  </p>
                </div>
              </div>

              <div class="product-item__text_group_secondary">
                <div class="product-item__spacer"></div>
                <a href="${product.url}" class="btn btn--small btn--full btn--tertiary">
                  Voir le produit
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Initialiser l'affichage des produits de marque
   */
  async function initMarqueProducts() {
    const containers = document.querySelectorAll('[data-marque-products]');

    if (!containers.length) return;

    for (const container of containers) {
      const brandGid = container.dataset.brandGid;
      const limit = parseInt(container.dataset.productLimit, 10) || 12;

      if (!brandGid) {
        console.warn('[Marque Products API] Pas de brand GID trouve');
        container.innerHTML = '<div class="marque-products-empty"><p>Aucun produit trouve.</p></div>';
        continue;
      }

      console.log('[Marque Products API] Initialisation pour GID:', brandGid, '| Limit:', limit);

      try {
        const products = await fetchProductsByBrand(brandGid, limit);

        if (products.length === 0) {
          container.innerHTML = '<div class="marque-products-empty"><p>Aucun produit trouve.</p></div>';

          // Cacher le bouton CTA s'il existe
          const ctaContainer = container.closest('.mq-products, .metaobject-marque-products')?.querySelector('.mq-products__cta-wrap, .marque-products-cta-container');
          if (ctaContainer) {
            ctaContainer.style.display = 'none';
          }
          continue;
        }

        // Generer le HTML des cartes produits avec index pour animation
        const cardsHtml = products.map((product, index) => renderProductCard(product, index)).join('');
        container.innerHTML = cardsHtml;

        // Ajouter les classes de grille du theme
        container.classList.add('product-item-holder');
        container.setAttribute('data-grid-style', 'default');
        container.setAttribute('data-product-item-alignment', 'center');
        container.setAttribute('data-standard-grid-spacing-type', 'product-list');

        // Afficher le CTA s'il y a potentiellement plus de produits
        const ctaContainer = container.closest('.mq-products, .metaobject-marque-products')?.querySelector('.mq-products__cta-wrap, .marque-products-cta-container');
        if (ctaContainer && products.length >= limit) {
          ctaContainer.style.display = '';
        }

        console.log('[Marque Products API] Produits affiches:', products.length);

      } catch (error) {
        console.error('[Marque Products API] Erreur initialisation:', error);
        container.innerHTML = '<div class="marque-products-empty"><p>Erreur lors du chargement des produits.</p></div>';
      }
    }
  }

  // Initialiser au chargement du DOM
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMarqueProducts);
  } else {
    initMarqueProducts();
  }

  // Export global pour debug
  window.MarqueProductsAPI = {
    fetchProductsByBrand,
    initMarqueProducts,
    BrandProductCache
  };

})();
